import { Stack, Typography } from '@ethings/ui-toolkit';
import { Box } from '@mui/system';
import { StaticDateTimePicker as BaseStaticDateTimePicker, TimeStepOptions } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { ElementType, FC, useEffect, useState } from 'react';
import { useResponsiveText } from '../../hooks';
import { Nullable } from '../../types';
import { formatDateTimeForDisplay, hourCycle, innerText } from '../../util';
import { Icon } from '../icons';

interface StaticDateTimePickerProps {
  defaultValue?: DateTime | null;
  futureDates?: boolean;
  icon?: ElementType;
  label?: string;
  onAccept?: ((value: DateTime | null) => void);
  onClose?: () => void;
  timeSteps?: TimeStepOptions;
  minDateTime?: DateTime;
  maxDateTime?: DateTime;

}

export const StaticDateTimePicker: FC<StaticDateTimePickerProps> = ({
  defaultValue = null,
  futureDates,
  icon,
  label,
  onAccept,
  onClose,
  timeSteps,
  minDateTime,
  maxDateTime,
}) => {
  const [value, setValue] = useState<Nullable<DateTime>>(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const displayValue = value ? formatDateTimeForDisplay(value) : '--';
  const h5Styles = useResponsiveText('1.5rem', innerText(displayValue).length, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  return (
    <Stack>
      <Box sx={{ p: 2 }}>
        {label && <Typography variant='caption'>{label}</Typography>}
        <Typography
          variant='h5'
          style={{
            fontSize: h5Styles.fontSize,
            lineHeight: h5Styles.lineHeight,
            wordBreak: h5Styles.wordBreak,
            whiteSpace: h5Styles.whiteSpace,
            overflow: h5Styles.overflow,
            textOverflow: h5Styles.textOverflow,
          }}
        >
          {displayValue}
        </Typography>
      </Box>
      <Box>
        <BaseStaticDateTimePicker
          ampm={hourCycle()}
          disableFuture={!futureDates}
          minutesStep={timeSteps?.minutes}
          onAccept={onAccept}
          onChange={setValue}
          onClose={onClose}
          minDateTime={minDateTime}
          maxDateTime={maxDateTime}
          slotProps={{
            toolbar: {
              hidden: true,
            },
            tabs: {
              dateIcon: icon ? <>{icon}</> : <Icon icon="CalendarIcon" />,
            },
          }}
          value={value}
        />
      </Box>
    </Stack>
  );
};
